import type { Component } from 'vue';
import type { SelectOption } from 'naive-ui';
import { IconBusiness, IconUser } from '@/components/design/icons';
import type { RouteLocationRaw } from 'vue-router';
import type { GaiaRemoteFetch } from '@/components/design/base/selects/GaiaRemoteSelect.vue';
import {
  type ContractManagementClauseCreate,
  type CreateContractManagementContractualPartyData,
  type ContractManagementContractualPartyLegalEntityInDb,
  type ContractManagementContractualPartyNaturalPersonInDb,
  type ContractManagementMatterCreate,
  type ContractManagementMatterInDb,
  type ContractManagementClauseInDb,
  type ContractManagementObligationCreate,
  type ContractManagementObligationInDb,
  type FilterableSearchQuery,
  searchContractManagementClauses,
  createContractManagementClause,
  searchContractManagementObligations,
  createContractManagementObligation,
  searchContractManagementMatters,
  createContractManagementMatter,
  searchContractManagementContractualParties,
  createContractManagementContractualParty,
  updateContractManagementContractualParty,
  addContractualPartyRepresentative,
  removeContractualPartyRepresentative,
  setContractualPartyDefaultRepresentative,
  getAvailableCountries,
  mergeContractManagementContractualParties,
  mergeContractManagementMatters,
  updateContractManagementDate,
  createContractManagementContractFileDate,
} from '@/api';
import { staticEntityName } from '@/utils/contractManagementView/staticEntityName';
import { formatDateInputDisplay } from '@/utils/dateInput';
import { ACCESS_PERMISSION_WRITE, allows } from '@/utils/accessPermission';
import {
  PARTY_KIND_LABELS,
  searchPartiesOfKind,
  toPartyRow,
  type ContractualParty,
} from '@/services/contractManagement/contractualPartyRow';
import {
  DATE_TYPE_LABELS,
  RECURRENCE_FREQUENCY_LABELS,
  RECURRENCE_TYPE_LABELS,
  dateBody,
  dateCreateBody,
  type DateRow,
} from '@/services/contractManagement/dateRow';
import { navLabels } from '@/components/design/domain/navigation/navLabels';
import { splitTags } from '@/components/design/domain/contractManagement/mergeTags';
import { ContractManagementMergeService } from '@/services/contractManagement/contractManagementMergeService';
import {
  entityDetailConfigs,
  type ContractManagementEntityType,
} from '@/types/contractManagement/detailConfig';
import {
  getClauseCategoryOptions,
  getMatterTypeOptions,
  getObligationTypeOptions,
} from '@/types/contractManagement/editConfig';
import { ContractualPartyShareService } from '@/services/contractualPartyShareService';

/**
 * One config per entity, one view for all of them — the shape the Entity List
 * Template mock uses (`<dc-import name="Entity List Template" entity-type="…">`).
 *
 * What lives here is only what exists nowhere else: the design's column set
 * (widths, order, default visibility, how each cell reads) and its list copy.
 * Everything the app already knows is reused rather than restated — the entity
 * key union, `displayName`, and the update/delete calls all come from
 * `entityDetailConfigs`; select options come from the generated enums. Only
 * search and create have no home yet, so they are bound here.
 *
 * Field names are checked against the generated model (see `defineEntityList`),
 * so a backend rename breaks the build rather than silently emptying a column.
 */

/** The subset of a row every entity shares; the rest is read through `key`. */
export interface EntityRow {
  uid: string;
  created_at: string;
  updated_at?: string | null;
  /** The caller's bits, on records that carry them (contractual parties); absent gates nothing. */
  permissions?: number | null;
  [key: string]: unknown;
}

/**
 * How a cell is drawn. `strong` is a secondary emphasised value; `chip` renders
 * a caps tag; `datetime` formats; the rest is plain muted text. The first
 * column is always the record's name and takes the ink/semibold treatment.
 */
export type EntityColumnKind = 'strong' | 'chip' | 'tags' | 'date' | 'datetime' | 'text';

export interface EntityColumn<TRow = EntityRow> {
  /** API field name — also the sort key sent to the backend. */
  key: keyof TRow & string;
  label: string;
  /** CSS grid track, straight from the mock (e.g. `minmax(180px,1.1fr)`). */
  width: string;
  sortable?: boolean;
  /** Always visible — the name column can't be hidden. */
  locked?: boolean;
  /** Shown by default; the rest start hidden in the columns menu. */
  def?: boolean;
  kind?: EntityColumnKind;
  /**
   * Display text, when the cell is not just `row[key]` — a code shown by its
   * label, say. `key` stays the real field so the column still sorts
   * server-side.
   */
  value?: (row: TRow) => string;
  /** Makes the cell a link — to the record it names. Null leaves it plain text. */
  link?: (row: TRow) => RouteLocationRaw | null;
  /** A glyph before the value — a party's kind, readable at a glance. */
  icon?: (row: TRow) => Component | undefined;
}

export interface EntityFilter<TRow = EntityRow> {
  key: keyof TRow & string;
  label: string;
  /**
   * Raw value → display label, for filters whose backend values are codes
   * (`legal_entity`, `DZA`). Display only: `column_filters` still sends the raw value.
   */
  labels?: () => Promise<Record<string, string>>;
}

export interface EntityField<TRow = EntityRow> {
  key: keyof TRow & string;
  label: string;
  required?: boolean;
  kind?: 'text' | 'select' | 'multiselect' | 'textarea' | 'email' | 'date' | 'number' | 'tags';
  hint?: string;
  placeholder?: string;
  /** Upper-cases as you type — country codes. */
  upper?: boolean;
  /** A text field offers these as you type, but takes whatever is written. */
  suggestions?: string[];
  maxLength?: number;
  /** Starting height of an `area`, in rows. The mock's default box is 3. */
  minRows?: number;
  /** Grid track for this field in its form row; defaults to an equal share. */
  width?: string;
  options?: SelectOption[];
  /** Options fetched once when the form opens — a short fixed list (countries). */
  loadOptions?: () => Promise<SelectOption[]>;
  /**
   * Server-side search for a `multiselect` over records that can run long —
   * handed to `GaiaRemoteSelect`, which debounces, pages and keeps pick labels.
   */
  searchOptions?: GaiaRemoteFetch;
  /** Labels for a `searchOptions` field's current values when editing. */
  selectedOptions?: (row: TRow) => SelectOption[];
  /** Shown (and validated) only while this holds for the form's values. */
  showWhen?: (values: Record<string, unknown>) => boolean;
  /** Settable on create only; read-only in the edit dialog. */
  createOnly?: boolean;
  /** Hidden on create — it picks among what the saved record already has. */
  editOnly?: boolean;
  /** How the detail page reads this field, when not just `row[key]` (a code's name). */
  display?: (row: TRow) => string;
  /** The detail page draws the value as these links instead (a party's linked parties). */
  links?: (row: TRow) => { label: string; to: RouteLocationRaw }[];
}

export interface EntitySearchResult {
  results: EntityRow[];
  result_count: number;
  filter_options: Record<string, (string | null)[]>;
}

/** Who a record is shared with, and at which access bitmask (READ=1, WRITE=2, ADMIN=4). */
export interface EntityShareRecipients {
  userUids: string[];
  roleUids: string[];
  permissions: number;
}

/**
 * The calls that have no existing config. Update and delete normally come off
 * `entityDetailConfigs`, which already binds them per entity.
 */
export interface EntityApi {
  search(query: FilterableSearchQuery, latestRevisionOnly: boolean): Promise<EntitySearchResult>;
  /** May hand back the created record, for callers that pick it straight away. */
  create(values: Record<string, unknown>): Promise<EntityRow | undefined>;
  /**
   * Overrides the `entityDetailConfigs` update, for types whose body it can't build.
   * `row` is the record as the editor opened it — what links are synced from.
   */
  update?: (uid: string, values: Record<string, unknown>, row?: EntityRow) => Promise<void>;
  /**
   * Share a just-created record with people and roles, so the create form can
   * grant access in the same save. Only for shareable types (contractual
   * parties); omit and the form shows no Access section.
   */
  share?: (uid: string, recipients: EntityShareRecipients) => Promise<void>;
  /**
   * Only for types the backend can merge; omit and the merge affordance hides.
   * May hand back the merged record, which the list then opens.
   */
  merge?(data: {
    leftUid: string;
    rightUid: string;
    mergedData: Record<string, unknown>;
  }): Promise<EntityRow | undefined>;
}

export interface EntityListConfig<TRow = EntityRow> {
  /** Key into `entityDetailConfigs` — also the detail route's `entityType` param. */
  type: ContractManagementEntityType;
  /** Plural page title. The singular comes from `entityDetailConfigs.displayName`. */
  plural: string;
  /** ['clause', 'clauses'] — for "3 clauses" counts. */
  unit: [string, string];
  /** Inert crumbs above the list crumb, outermost first. */
  crumbs: readonly string[];
  /** SVG path drawn in the empty state's dashed box — the type's sidebar glyph. */
  icon: string;
  searchPlaceholder: string;
  createLabel: string;
  /**
   * Create opens a menu of presets instead of the form straight away — a party
   * picks its kind here rather than in a field. Each option's `defaults` seed the form.
   */
  createOptions?: { key: string; label: string; icon: Component; defaults: EntityFormValues }[];
  emptyTitle: string;
  emptyBody: string;
  /** Width of the create/edit drawer. */
  formWidth: string;
  /** Offer the latest-revision-only toggle. */
  revisions: boolean;
  columns: EntityColumn<TRow>[];
  filters: EntityFilter<TRow>[];
  /**
   * Rows merge only when this returns the same for both — a contractual
   * party merges with its own kind. Omit and any two rows merge.
   */
  mergeMatch?: (row: TRow) => string;
  /** Field keys the merge dialog reconciles; a key whose `showWhen` fails for the pair is skipped. */
  mergeFields: (keyof TRow & string)[];
  /** Form rows — each inner array is one row of fields. */
  fields: EntityField<TRow>[][];
  /**
   * Extra fields folded behind a "More …" toggle, inserted after `afterRow`
   * and followed by a divider — skipped whole when none of its fields'
   * `showWhen` holds. Contractual Party has one: the four
   * rarer name parts, which would otherwise dominate a form whose real subject is
   * first and last name.
   */
  collapsible?: {
    afterRow: number;
    label: string;
    expandedLabel: string;
    fields: EntityField<TRow>[];
  };
  /**
   * Cross-field rule the per-field `required` flags can't express — a person
   * needs *one of* four name parts. Returns field key → message.
   */
  validate?: (values: Record<string, string>) => Record<string, string>;
  api: EntityApi;
}

/**
 * Authors a config against a generated model, then erases the row type so the
 * registry stays uniform. The erasure is safe — every `key` was checked as
 * `keyof TRow` on the way in, which is the whole point of the helper.
 */
function defineEntityList<TRow>(config: EntityListConfig<TRow>): EntityListConfig {
  return config as EntityListConfig;
}

/**
 * Narrow one field of a row or a form to a display string. Row values are
 * `unknown` (the shape differs per entity), so every read goes through here
 * rather than `String(…)`, which would happily stringify an object.
 */
export function toText(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) return value.map(item => toText(item)).join(', ');
  return '';
}

/** Form values: a `tags` / `multiselect` field holds an array, the rest a string. */
export type EntityFormValues = Record<string, string | string[]>;

/** Every field a config edits — its rows, then its collapsible group. */
/** Every field in reading order — the folded group sits where the form shows it. */
export function allFieldsOf(config: EntityListConfig): EntityField[] {
  const extra = config.collapsible;
  return config.fields.flatMap((row, index) =>
    extra && index === extra.afterRow ? [...row, ...extra.fields] : row
  );
}

const isListField = (field: EntityField): boolean =>
  field.kind === 'tags' || field.kind === 'multiselect';

/** A field whose `showWhen` fails is neither drawn nor validated. */
export function isFieldShown(field: EntityField, values: Record<string, unknown>): boolean {
  return field.showWhen?.(values) ?? true;
}

/** A stored record as form values — what an edit starts from. */
export function rowValues(config: EntityListConfig, row: EntityRow): EntityFormValues {
  return Object.fromEntries(
    allFieldsOf(config).map(field => {
      if (!isListField(field)) return [field.key, toText(row[field.key])];
      const raw = row[field.key];
      return [field.key, Array.isArray(raw) ? raw.map(item => toText(item)).filter(Boolean) : []];
    })
  );
}

/** Required / length checks on the shown text fields, then the config's own rule. */
export function validateValues(
  config: EntityListConfig,
  values: EntityFormValues
): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const field of allFieldsOf(config)) {
    if (isListField(field) || !isFieldShown(field, values)) continue;
    const value = toText(values[field.key]).trim();
    if (field.required && !value) errors[field.key] = `${field.label} is required`;
    else if (field.maxLength && value.length > field.maxLength) {
      errors[field.key] = `${field.label} must be ${field.maxLength} characters`;
    }
  }
  const text = Object.fromEntries(Object.keys(values).map(key => [key, toText(values[key])]));
  return { ...errors, ...config.validate?.(text) };
}

/**
 * The singular label — `entityDetailConfigs` already carries it per type. A
 * type without a detail page (date) falls back to its unit.
 */
export function entityLabel(config: EntityListConfig): string {
  if (config.type in entityDetailConfigs) return entityDetailConfigs[config.type].displayName;
  const singular = config.unit[0];
  return singular.charAt(0).toUpperCase() + singular.slice(1);
}

/**
 * Update and delete for one entity, off the config the detail view already
 * uses. Both are optional there — `view` has neither — so each is checked
 * before it is called.
 */
export async function updateEntity(
  config: EntityListConfig,
  uid: string,
  values: Record<string, unknown>,
  row?: EntityRow
): Promise<void> {
  if (config.api.update) return config.api.update(uid, values, row);
  const update = entityDetailConfigs[config.type].updateFunction;
  if (!update) throw new Error(`${entityLabel(config)} cannot be updated`);
  await update(uid, values);
}

export async function deleteEntity(config: EntityListConfig, uid: string): Promise<void> {
  const remove = entityDetailConfigs[config.type].deleteFunction;
  if (!remove) throw new Error(`${entityLabel(config)} cannot be deleted`);
  await remove(uid);
}

/**
 * Clause. The name column is `title`, not `name` — the mock renames the field
 * accordingly, and the Text column is the widest track on any entity.
 */
export const clauseConfig = defineEntityList<ContractManagementClauseInDb>({
  type: 'clause',
  plural: navLabels.clauses,
  unit: ['clause', 'clauses'],
  crumbs: [navLabels.repository],
  icon: 'M4 2.5h5l3 3v8H4zM9 2.5v3h3M6 8.5h4M6 10.8h3',
  searchPlaceholder: 'Search clauses',
  createLabel: 'Create clause',
  emptyTitle: 'No clauses yet',
  emptyBody:
    'Clauses are pulled out of contract files as they’re analysed, and can be reused as templating and review references. Add one by hand to start a house-standard library.',
  formWidth: '540px',
  revisions: true,
  columns: [
    { key: 'title', label: 'Title', width: 'minmax(160px,1fr)', sortable: true, locked: true },
    { key: 'category', label: 'Category', width: '160px', sortable: true, def: true, kind: 'chip' },
    { key: 'text', label: 'Text', width: 'minmax(230px,1.9fr)', def: true },
    { key: 'description', label: 'Description', width: 'minmax(180px,1.2fr)' },
    { key: 'clause_number', label: 'Clause no.', width: '110px', def: true },
    { key: 'section_path', label: 'Section path', width: '150px' },
    {
      key: 'created_at',
      label: 'Created',
      width: '160px',
      sortable: true,
      def: true,
      kind: 'datetime',
    },
    { key: 'updated_at', label: 'Updated', width: '160px', def: true, kind: 'datetime' },
  ],
  filters: [{ key: 'category', label: 'Category' }],
  mergeFields: ['title', 'category', 'text', 'clause_number'],
  fields: [
    [{ key: 'title', label: 'Title', required: true, placeholder: 'Confidentiality' }],
    [
      {
        key: 'text',
        label: 'Text',
        required: true,
        kind: 'textarea',
        // The mock gives this box 110px against the 72px default.
        minRows: 5,
        placeholder: 'Paste the clause wording',
      },
    ],
    [
      {
        key: 'category',
        label: 'Category',
        required: true,
        kind: 'select',
        options: getClauseCategoryOptions(),
      },
    ],
    [
      { key: 'clause_number', label: 'Clause number', placeholder: '§ 8.1' },
      { key: 'section_path', label: 'Section path', placeholder: 'II / B / 3' },
    ],
    [
      {
        key: 'description',
        label: 'Description',
        kind: 'textarea',
        hint: 'Optional',
        placeholder: 'When to use this clause',
      },
    ],
  ],
  api: {
    async search(query, latestRevisionOnly): Promise<EntitySearchResult> {
      const response = await searchContractManagementClauses({
        body: query,
        query: { latest_revision_only: latestRevisionOnly },
      });
      if (!response.data) throw new Error('Failed to search clauses');
      const result = response.data;
      return {
        results: result.results as unknown as EntityRow[],
        result_count: result.result_count,
        filter_options: result.filter_options as Record<string, (string | null)[]>,
      };
    },
    async create(values): Promise<undefined> {
      await createContractManagementClause({
        body: {
          title: toText(values.title).trim() || null,
          text: toText(values.text).trim(),
          category: values.category as ContractManagementClauseCreate['category'],
          description: toText(values.description).trim() || null,
          clause_number: toText(values.clause_number).trim() || null,
          section_path: toText(values.section_path).trim() || null,
        },
      });
    },
    async merge(data): Promise<undefined> {
      await ContractManagementMergeService.mergeClauses(data);
    },
  },
});

/** Obligation. The narrowest of the six — four content columns, all on by default. */
export const obligationConfig = defineEntityList<ContractManagementObligationInDb>({
  type: 'obligation',
  plural: navLabels.obligations,
  unit: ['obligation', 'obligations'],
  crumbs: [navLabels.repository],
  icon: 'M3 3h10v10H3zM5.6 8.2l1.7 1.7 3.1-3.6',
  searchPlaceholder: 'Search obligations',
  createLabel: 'Create obligation',
  emptyTitle: 'No obligations yet',
  emptyBody:
    'Obligations are the duties, rights and conditions a contract creates. They’re extracted with their trigger events, or add one to track something the analysis missed.',
  formWidth: '500px',
  revisions: true,
  columns: [
    { key: 'name', label: 'Name', width: 'minmax(170px,1fr)', sortable: true, locked: true },
    { key: 'type', label: 'Type', width: '130px', sortable: true, def: true, kind: 'chip' },
    { key: 'trigger_event', label: 'Trigger event', width: 'minmax(150px,1fr)', def: true },
    { key: 'description', label: 'Description', width: 'minmax(200px,1.5fr)', def: true },
    {
      key: 'created_at',
      label: 'Created',
      width: '160px',
      sortable: true,
      def: true,
      kind: 'datetime',
    },
    { key: 'updated_at', label: 'Updated', width: '160px', def: true, kind: 'datetime' },
  ],
  filters: [{ key: 'type', label: 'Type' }],
  mergeFields: ['name', 'type', 'trigger_event', 'description'],
  fields: [
    [{ key: 'name', label: 'Name', required: true, placeholder: 'Rent payment' }],
    [
      {
        key: 'type',
        label: 'Type',
        required: true,
        kind: 'select',
        options: getObligationTypeOptions(),
      },
      {
        key: 'trigger_event',
        label: 'Trigger event',
        placeholder: 'Monthly, 3rd business day',
      },
    ],
    [
      {
        key: 'description',
        label: 'Description',
        kind: 'textarea',
        hint: 'Optional',
        placeholder: 'What is owed, by whom, to whom',
      },
    ],
  ],
  api: {
    async search(query, latestRevisionOnly): Promise<EntitySearchResult> {
      const response = await searchContractManagementObligations({
        body: query,
        query: { latest_revision_only: latestRevisionOnly },
      });
      if (!response.data) throw new Error('Failed to search obligations');
      const result = response.data;
      return {
        results: result.results as unknown as EntityRow[],
        result_count: result.result_count,
        filter_options: result.filter_options as Record<string, (string | null)[]>,
      };
    },
    async create(values): Promise<undefined> {
      await createContractManagementObligation({
        body: {
          name: toText(values.name).trim(),
          type: values.type as ContractManagementObligationCreate['type'],
          trigger_event: toText(values.trigger_event).trim() || null,
          description: toText(values.description).trim() || null,
        },
      });
    },
    async merge(data): Promise<undefined> {
      await ContractManagementMergeService.mergeObligations(data);
    },
  },
});

/**
 * Create and merge body. Tags arrive as an array from the form and as a
 * comma-joined string from the merge dialog.
 */
function matterBody(values: Record<string, unknown>): ContractManagementMatterCreate {
  return {
    name: toText(values.name).trim(),
    type: values.type as ContractManagementMatterCreate['type'],
    tags: Array.isArray(values.tags)
      ? values.tags.map(tag => toText(tag))
      : splitTags(toText(values.tags)),
    description: toText(values.description).trim() || null,
  };
}

/**
 * Matter. The odd one out: no revision toggle, it files under Contract
 * Management rather than Entities, and its tags are a real chip input rather
 * than a comma-separated string.
 */
export const matterConfig = defineEntityList<ContractManagementMatterInDb>({
  type: 'matter',
  plural: navLabels.matters,
  unit: ['matter', 'matters'],
  crumbs: [navLabels.repository],
  icon: 'M3.5 2.5h6l3 3v8h-9zM9 2.5v3h3M6 9h4',
  searchPlaceholder: 'Search matters',
  createLabel: 'Create matter',
  emptyTitle: 'No matters yet',
  emptyBody:
    'Matters group everything belonging to one piece of legal work — files, reviews and signatures. Create the first one to start filing against it.',
  formWidth: '500px',
  revisions: false,
  columns: [
    { key: 'name', label: 'Name', width: 'minmax(170px,1fr)', sortable: true, locked: true },
    { key: 'type', label: 'Type', width: '170px', sortable: true, def: true, kind: 'chip' },
    { key: 'tags', label: 'Tags', width: 'minmax(160px,1fr)', def: true, kind: 'tags' },
    { key: 'description', label: 'Description', width: 'minmax(200px,1.4fr)', def: true },
    {
      key: 'created_at',
      label: 'Created',
      width: '160px',
      sortable: true,
      def: true,
      kind: 'datetime',
    },
    { key: 'updated_at', label: 'Updated', width: '160px', def: true, kind: 'datetime' },
  ],
  filters: [
    { key: 'type', label: 'Type' },
    { key: 'tags', label: 'Tag' },
  ],
  mergeFields: ['name', 'type', 'tags', 'description'],
  fields: [
    [{ key: 'name', label: 'Name', required: true, placeholder: 'Project Kova' }],
    [
      {
        key: 'type',
        label: 'Type',
        required: true,
        kind: 'select',
        options: getMatterTypeOptions(),
      },
    ],
    [{ key: 'tags', label: 'Tags', kind: 'tags', hint: 'Enter to add', placeholder: 'Add a tag' }],
    [
      {
        key: 'description',
        label: 'Description',
        kind: 'textarea',
        hint: 'Optional',
        placeholder: 'What this matter covers',
      },
    ],
  ],
  api: {
    // Matters are not revisioned — their search endpoint has no
    // `latest_revision_only`, which is why `revisions` is false above.
    async search(query): Promise<EntitySearchResult> {
      const response = await searchContractManagementMatters({ body: query });
      if (!response.data) throw new Error('Failed to search matters');
      const result = response.data;
      return {
        results: result.results as unknown as EntityRow[],
        result_count: result.result_count,
        filter_options: result.filter_options as Record<string, (string | null)[]>,
      };
    },
    // Returns the matter so a caller can assign it at once (the upload history).
    async create(values): Promise<EntityRow> {
      const { data } = await createContractManagementMatter({
        body: matterBody(values),
        throwOnError: true,
      });
      return data;
    },
    // The merge creates a new matter (new uid) holding both's contracts and
    // deletes both sources.
    async merge({ leftUid, rightUid, mergedData }): Promise<EntityRow> {
      const { data } = await mergeContractManagementMatters({
        query: { left_model_uid: leftUid, right_model_uid: rightUid },
        body: matterBody(mergedData),
        throwOnError: true,
      });
      return data;
    },
  },
});

/**
 * Contractual party — one record per organisation, reused across contracts,
 * in two kinds. The list shows both; the form swaps its fields on `kind`.
 * `name` is computed server-side (a company's `legal_name`, a person's name
 * parts), so it sorts and searches the same for both. Only base-table columns
 * sort — `name`, `iso_3166_alpha_3`, the timestamps; a person-only `email` doesn't.
 */
// Both kinds' keys in one row type, so every field below is checked against the model.
type ContractualPartyRow = Omit<ContractManagementContractualPartyLegalEntityInDb, 'kind'> &
  Partial<Omit<ContractManagementContractualPartyNaturalPersonInDb, 'kind'>> & {
    kind?: 'natural_person' | 'legal_entity';
    /** Flattened off `representatives` (and, on the detail page, a person's companies). */
    representative_uids?: string[];
    representative_names?: string;
    represented_uids?: string[];
    represented_names?: string;
    represented_parties?: ContractualParty[];
    default_representative_uid?: string;
  };

/** A linked party's detail page. */
function partyLink(party: { uid: string; name: string }): { label: string; to: RouteLocationRaw } {
  return {
    label: party.name,
    to: {
      name: 'contract-management-detail',
      params: { entityType: 'contractual-party', uid: party.uid },
    },
  };
}

const isPerson = (values: Record<string, unknown>): boolean => values.kind === 'natural_person';
const isCompany = (values: Record<string, unknown>): boolean => values.kind === 'legal_entity';

const PARTY_KIND_OPTIONS: SelectOption[] = Object.entries(PARTY_KIND_LABELS).map(
  ([value, label]) => ({ label, value })
);

const PARTY_ADDRESS_KEYS = ['street_address', 'postcode', 'city', 'iso_3166_alpha_3'] as const;
const PARTY_PERSON_KEYS = [
  'email',
  'title',
  'first_name',
  'middle_name',
  'last_name',
  'suffix',
  'artist_name',
  'date_of_birth',
  'anonymisation_id',
  'personal_id',
  'issuing_authority',
] as const;
/** The common legal forms; the field still takes any other. */
const LEGAL_FORM_SUGGESTIONS = [
  'GmbH',
  'GmbH & Co. KG',
  'UG (haftungsbeschränkt)',
  'AG',
  'KG',
  'OHG',
  'GbR',
  'PartG mbB',
  'eG',
  'e.V.',
  'SE',
  'SARL',
  'SCS',
  'SCSp',
  'S.A.',
  'Ltd',
  'LP',
  'LLC',
  'Inc.',
];

const PARTY_COMPANY_KEYS = ['legal_form', 'commercial_register', 'registration_number'] as const;

/** The kind's own fields, trimmed, blanks as null — the create/update body. */
function partyBody(
  values: Record<string, unknown>
): CreateContractManagementContractualPartyData['body'] {
  const pick = (keys: readonly string[]): Record<string, string | null> =>
    Object.fromEntries(keys.map(key => [key, toText(values[key]).trim() || null]));
  const shared = pick(PARTY_ADDRESS_KEYS);
  return isPerson(values)
    ? { kind: 'natural_person' as const, ...shared, ...pick(PARTY_PERSON_KEYS) }
    : {
        kind: 'legal_entity' as const,
        ...shared,
        ...pick(PARTY_COMPANY_KEYS),
        legal_name: toText(values.legal_name).trim(),
      };
}

/** The country dropdown's options — a fixed ISO list, so fetched once per session. */
let countryOptions: Promise<SelectOption[]> | null = null;

function loadCountryOptions(): Promise<SelectOption[]> {
  countryOptions ??= getAvailableCountries({ throwOnError: true }).then(({ data }) =>
    data.map(country => ({ label: country.name, value: country.iso_3166_alpha_3 }))
  );
  // A failed fetch isn't cached, so the next form open retries it.
  countryOptions.catch(() => (countryOptions = null));
  return countryOptions;
}

/** Alpha-3 code → country name, for the country filter's chips. */
async function loadCountryLabels(): Promise<Record<string, string>> {
  const options = await loadCountryOptions();
  return Object.fromEntries(options.map(option => [toText(option.value), toText(option.label)]));
}

function uidsOf(values: Record<string, unknown>, key: string): string[] {
  const raw = values[key];
  return Array.isArray(raw) ? raw.map(uid => toText(uid)) : [];
}

/**
 * Bring a party's representative links to `wanted`, one call per change. The
 * link is one table seen from both ends, so `pair` says which side `uid` is:
 * a company syncs its representatives, a person the companies it represents.
 */
async function syncLinks(
  current: string[],
  wanted: string[],
  pair: (uid: string) => { legal_entity_uid: string; representative_uid: string }
): Promise<void> {
  await Promise.all([
    ...wanted
      .filter(uid => !current.includes(uid))
      .map(uid => addContractualPartyRepresentative({ query: pair(uid), throwOnError: true })),
    ...current
      .filter(uid => !wanted.includes(uid))
      .map(uid => removeContractualPartyRepresentative({ query: pair(uid), throwOnError: true })),
  ]);
}

/**
 * Sync whichever side of the representative link this party's kind owns. A
 * person's companies aren't embedded, so its side syncs from `represented` —
 * none on create, the detail page's loaded list on edit — and is left alone
 * when the editor never loaded them (the list page, the signing drawers).
 */
async function syncPartyLinks(
  party: ContractualParty,
  values: Record<string, unknown>,
  represented: string[] | undefined
): Promise<void> {
  if (party.kind === 'legal_entity') {
    const current = (party.representatives ?? []).map(person => person.uid);
    const wanted = uidsOf(values, 'representative_uids');
    await syncLinks(current, wanted, uid => ({
      legal_entity_uid: party.uid,
      representative_uid: uid,
    }));
    // A default dropped from the representatives is cleared by the backend.
    const wantedDefault = toText(values.default_representative_uid);
    if (
      wantedDefault !== (party.default_representative?.uid ?? '') &&
      (!wantedDefault || wanted.includes(wantedDefault))
    ) {
      await setContractualPartyDefaultRepresentative({
        query: { legal_entity_uid: party.uid, representative_uid: wantedDefault || null },
        throwOnError: true,
      });
    }
  } else if (represented) {
    await syncLinks(represented, uidsOf(values, 'represented_uids'), uid => ({
      legal_entity_uid: uid,
      representative_uid: party.uid,
    }));
  }
}

export const contractualPartyConfig = defineEntityList<ContractualPartyRow>({
  type: 'contractual-party',
  plural: navLabels.contractualParties,
  unit: ['party', 'parties'],
  // A top-level sidebar row of its own, so nothing sits above it in the trail.
  crumbs: [],
  icon: 'M5.5 3.2a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM1.8 12.8c.4-2 1.9-3.1 3.7-3.1s3.3 1.1 3.7 3.1M9.5 6.5h5v6.3h-5z',
  searchPlaceholder: 'Search parties',
  createLabel: 'New',
  createOptions: [
    {
      key: 'natural_person',
      label: 'New Natural Person',
      icon: IconUser,
      defaults: { kind: 'natural_person' },
    },
    {
      key: 'legal_entity',
      label: 'New Legal Entity',
      icon: IconBusiness,
      defaults: { kind: 'legal_entity' },
    },
  ],
  emptyTitle: 'No contractual parties yet',
  emptyBody:
    'Parties are the people and companies a contract is concluded between. Each is stored once and reused across contracts.',
  formWidth: '540px',
  revisions: false,
  columns: [
    {
      key: 'name',
      label: 'Name',
      width: 'minmax(190px,1.25fr)',
      sortable: true,
      locked: true,
      icon: (row): Component => (row.kind === 'natural_person' ? IconUser : IconBusiness),
    },
    {
      key: 'kind',
      label: 'Kind',
      width: '150px',
      def: true,
      kind: 'chip',
      value: (row): string => PARTY_KIND_LABELS[row.kind ?? ''] ?? '',
    },
    { key: 'email', label: 'Email', width: 'minmax(160px,1fr)', def: true },
    { key: 'city', label: 'City', width: '130px', def: true },
    {
      // Sorts on the stored code; shows the name the backend resolves for it.
      key: 'iso_3166_alpha_3',
      label: 'Country',
      width: '150px',
      sortable: true,
      def: true,
      value: (row): string => row.country_name ?? row.iso_3166_alpha_3 ?? '',
    },
    { key: 'representative_names', label: 'Represented by', width: '170px', def: true },
    { key: 'street_address', label: 'Street', width: 'minmax(160px,1fr)' },
    { key: 'postcode', label: 'Postcode', width: '100px' },
    {
      key: 'created_at',
      label: 'Created',
      width: '160px',
      sortable: true,
      def: true,
      kind: 'datetime',
    },
    { key: 'updated_at', label: 'Updated', width: '160px', kind: 'datetime' },
  ],
  filters: [
    {
      key: 'kind',
      label: 'Kind',
      labels: (): Promise<Record<string, string>> => Promise.resolve(PARTY_KIND_LABELS),
    },
    { key: 'iso_3166_alpha_3', label: 'Country', labels: loadCountryLabels },
  ],
  // Both kinds' fields; the dialog keeps the ones the pair's kind shows.
  // Contract-file and representative links aren't reconciled — the backend
  // carries both parties' links over to the merged one.
  mergeMatch: (row): string => row.kind ?? '',
  mergeFields: [
    'legal_name',
    'first_name',
    'last_name',
    'title',
    'middle_name',
    'artist_name',
    'suffix',
    'date_of_birth',
    'email',
    'street_address',
    'postcode',
    'city',
    'iso_3166_alpha_3',
    'legal_form',
    'commercial_register',
    'registration_number',
    'personal_id',
    'issuing_authority',
    'anonymisation_id',
  ],
  fields: [
    [
      {
        key: 'kind',
        label: 'Kind',
        required: true,
        kind: 'select',
        options: PARTY_KIND_OPTIONS,
        createOnly: true,
        // Picked before the form opens (the create menu, or the caller's preset).
        editOnly: true,
      },
    ],
    [
      {
        key: 'legal_name',
        label: 'Legal name',
        required: true,
        placeholder: 'TIER SE',
        showWhen: isCompany,
      },
    ],
    [
      { key: 'first_name', label: 'First name', placeholder: 'John', showWhen: isPerson },
      { key: 'last_name', label: 'Last name', placeholder: 'Doe', showWhen: isPerson },
    ],
    [
      {
        key: 'date_of_birth',
        label: 'Date of birth',
        kind: 'date',
        hint: 'Optional',
        showWhen: isPerson,
      },
      {
        key: 'email',
        label: 'Email',
        kind: 'email',
        hint: 'Optional',
        placeholder: 'name@company.com',
        showWhen: isPerson,
      },
    ],
    [
      {
        key: 'legal_form',
        label: 'Legal form',
        placeholder: 'GmbH',
        suggestions: LEGAL_FORM_SUGGESTIONS,
        showWhen: isCompany,
      },
      {
        key: 'commercial_register',
        label: 'Commercial register',
        placeholder: 'Handelsregister Berlin',
        showWhen: isCompany,
      },
      {
        key: 'registration_number',
        label: 'Registration number',
        placeholder: 'HRB 12345',
        showWhen: isCompany,
      },
    ],
    [{ key: 'street_address', label: 'Street address', placeholder: 'Friedrichstraße 68' }],
    [
      { key: 'postcode', label: 'Postcode', placeholder: '10117', width: '110px' },
      { key: 'city', label: 'City', placeholder: 'Berlin' },
      {
        key: 'iso_3166_alpha_3',
        label: 'Country',
        kind: 'select',
        placeholder: 'Select a country',
        loadOptions: loadCountryOptions,
        display: (row): string => row.country_name ?? row.iso_3166_alpha_3 ?? '',
      },
    ],
    [
      {
        key: 'personal_id',
        label: 'Personal ID',
        placeholder: 'Passport / national ID',
        showWhen: isPerson,
      },
      {
        key: 'issuing_authority',
        label: 'Issuing authority',
        placeholder: 'Stadt Berlin',
        showWhen: isPerson,
      },
    ],
    [
      {
        key: 'anonymisation_id',
        label: 'Anonymisation ID',
        hint: 'Used in redacted exports',
        placeholder: 'e.g. EMP-014',
        showWhen: isPerson,
      },
    ],
    [
      {
        key: 'representative_uids',
        label: 'Represented by',
        kind: 'multiselect',
        hint: 'Natural person parties',
        placeholder: 'Pick representatives',
        showWhen: isCompany,
        display: (row): string => row.representative_names ?? '',
        links: (row): { label: string; to: RouteLocationRaw }[] =>
          (row.representatives ?? []).map(partyLink),
        searchOptions: searchPartiesOfKind('natural_person'),
        // Labels for the current picks, which needn't be on the page loaded.
        selectedOptions: (row): SelectOption[] =>
          (row.representatives ?? []).map(person => ({ label: person.name, value: person.uid })),
      },
      {
        key: 'default_representative_uid',
        label: 'Default signer',
        kind: 'select',
        hint: 'Preselected when signing',
        placeholder: 'Pick a representative',
        showWhen: isCompany,
        editOnly: true,
        display: (row): string => row.default_representative?.name ?? '',
        // ponytail: offers saved representatives only; one added in the same
        // edit is pickable after saving (the first one becomes the default anyway).
        selectedOptions: (row): SelectOption[] =>
          (row.representatives ?? []).map(person => ({ label: person.name, value: person.uid })),
      },
      {
        key: 'represented_uids',
        label: 'Represents',
        kind: 'multiselect',
        hint: 'Legal entity parties',
        placeholder: 'Pick legal entities',
        showWhen: isPerson,
        display: (row): string => row.represented_names ?? '',
        links: (row): { label: string; to: RouteLocationRaw }[] =>
          (row.represented_parties ?? []).map(partyLink),
        // Linking or unlinking changes the company, so it needs edit access
        // there: such companies can be neither picked nor removed.
        searchOptions: searchPartiesOfKind('legal_entity', { requireWrite: true }),
        selectedOptions: (row): SelectOption[] =>
          (row.represented_parties ?? []).map(entity => ({
            label: entity.name,
            value: entity.uid,
            disabled: !allows(entity.permissions, ACCESS_PERMISSION_WRITE),
          })),
      },
    ],
  ],
  // The rarer name parts fold away after first / last name.
  collapsible: {
    afterRow: 2,
    label: 'More name fields',
    expandedLabel: 'Fewer name fields',
    fields: [
      { key: 'title', label: 'Title', placeholder: 'Dr.', showWhen: isPerson },
      { key: 'middle_name', label: 'Middle name', placeholder: 'Melanie', showWhen: isPerson },
      {
        key: 'artist_name',
        label: 'Artist / stage name',
        placeholder: 'Used when no legal name is known',
        showWhen: isPerson,
      },
      { key: 'suffix', label: 'Suffix', placeholder: 'Jr.', showWhen: isPerson },
    ],
  },
  // Same rule the backend enforces: a person needs one actual name part.
  validate(values): Record<string, string> {
    if (values.kind !== 'natural_person') return {};
    const named = ['first_name', 'middle_name', 'last_name', 'artist_name'].some(key =>
      (values[key] ?? '').trim()
    );
    return named
      ? {}
      : { first_name: 'Enter at least a first, middle, last, or artist / stage name.' };
  },
  api: {
    async search(query): Promise<EntitySearchResult> {
      const { data } = await searchContractManagementContractualParties({
        body: query,
        throwOnError: true,
      });
      return {
        results: data.results.map(party => toPartyRow(party)),
        result_count: data.result_count,
        filter_options: data.filter_options as Record<string, (string | null)[]>,
      };
    },
    async create(values): Promise<EntityRow> {
      const { data } = await createContractManagementContractualParty({
        body: partyBody(values),
        throwOnError: true,
      });
      await syncPartyLinks(data, values, []);
      return toPartyRow(data);
    },
    async update(uid, values, row): Promise<void> {
      const { data } = await updateContractManagementContractualParty({
        path: { uid },
        body: partyBody(values),
        throwOnError: true,
      });
      const represented = Array.isArray(row?.represented_uids)
        ? uidsOf(row, 'represented_uids')
        : undefined;
      await syncPartyLinks(data, values, represented);
    },
    async share(uid, recipients): Promise<void> {
      await ContractualPartyShareService.bulkShareParties({ partyUids: [uid], ...recipients });
    },
    // The merge creates a new party (new uid) and deletes both sources.
    async merge({ leftUid, rightUid, mergedData }): Promise<EntityRow> {
      const { data } = await mergeContractManagementContractualParties({
        query: { left_model_uid: leftUid, right_model_uid: rightUid },
        body: partyBody(mergedData),
        throwOnError: true,
      });
      return toPartyRow(data);
    },
  },
});

/** Calendar glyph shared by the two date entities. */
const DATE_ICON = 'M3 4.5h10v9H3zM3 7.5h10M6 2.5v3M10 2.5v3';

const labelOptions = (labels: Record<string, string>): SelectOption[] =>
  Object.entries(labels).map(([value, label]) => ({ label, value }));

const repeats = (values: Record<string, unknown>): boolean =>
  toText(values.recurrence_type) !== '' && toText(values.recurrence_type) !== 'never';

/**
 * Date. No name of its own — the date is the record, so it is also the lead
 * column. Its type says which end of the term it marks; its recurrence is
 * flattened onto the row (`toDateRow`) so each part edits as its own field.
 * No merge: the backend has no endpoint for it. Only the contract file's term
 * panels use it — there is no list page — and a date is created on a file, so
 * creating goes through `fileDateConfig`.
 */
export const dateConfig = defineEntityList<DateRow>({
  type: 'date',
  plural: navLabels.dates,
  unit: ['date', 'dates'],
  crumbs: [navLabels.repository],
  icon: DATE_ICON,
  searchPlaceholder: 'Search dates',
  createLabel: 'Create date',
  emptyTitle: 'No dates yet',
  emptyBody:
    'Dates mark when a contract’s term takes effect and when it runs out. They’re extracted automatically, or add one by hand to link it to a contract file.',
  formWidth: '440px',
  revisions: true,
  columns: [
    {
      key: 'date',
      label: 'Date',
      width: 'minmax(160px,0.8fr)',
      sortable: true,
      locked: true,
      kind: 'date',
      value: (row): string => staticEntityName(row),
    },
    {
      key: 'type',
      label: 'Type',
      width: '150px',
      sortable: true,
      def: true,
      kind: 'chip',
      value: (row): string => DATE_TYPE_LABELS[row.type] ?? row.type,
    },
    { key: 'description', label: 'Description', width: 'minmax(200px,1.8fr)', def: true },
    {
      key: 'created_at',
      label: 'Created',
      width: '160px',
      sortable: true,
      def: true,
      kind: 'datetime',
    },
    { key: 'updated_at', label: 'Updated', width: '160px', def: true, kind: 'datetime' },
  ],
  filters: [
    {
      key: 'type',
      label: 'Type',
      labels: (): Promise<Record<string, string>> => Promise.resolve(DATE_TYPE_LABELS),
    },
  ],
  mergeFields: [],
  fields: [
    [
      {
        key: 'type',
        label: 'Type',
        required: true,
        kind: 'select',
        options: labelOptions(DATE_TYPE_LABELS),
        // The update takes no type: to change it, delete the date and add another.
        createOnly: true,
        display: (row): string => DATE_TYPE_LABELS[row.type] ?? row.type,
      },
    ],
    [
      {
        key: 'date',
        label: 'Date',
        kind: 'date',
        hint: 'Optional',
        display: (row): string => (row.date ? formatDateInputDisplay(row.date) : ''),
      },
    ],
    [
      {
        key: 'recurrence_type',
        label: 'Repeats',
        required: true,
        kind: 'select',
        options: labelOptions(RECURRENCE_TYPE_LABELS),
        display: (row): string =>
          RECURRENCE_TYPE_LABELS[row.recurrence_type] ?? toText(row.recurrence_type),
      },
    ],
    [
      {
        key: 'recurrence_interval',
        label: 'Every',
        required: true,
        kind: 'number',
        placeholder: '1',
        showWhen: repeats,
      },
      {
        key: 'recurrence_frequency',
        label: 'Frequency',
        required: true,
        kind: 'select',
        options: labelOptions(RECURRENCE_FREQUENCY_LABELS),
        showWhen: repeats,
        display: (row): string =>
          RECURRENCE_FREQUENCY_LABELS[row.recurrence_frequency ?? ''] ??
          toText(row.recurrence_frequency),
      },
    ],
    [
      {
        key: 'recurrence_count',
        label: 'Occurrences',
        required: true,
        kind: 'number',
        showWhen: (values): boolean => toText(values.recurrence_type) === 'count',
      },
    ],
    [
      {
        key: 'recurrence_until_date',
        label: 'Until',
        required: true,
        kind: 'date',
        showWhen: (values): boolean => toText(values.recurrence_type) === 'until_date',
        display: (row): string =>
          row.recurrence_until_date ? formatDateInputDisplay(row.recurrence_until_date) : '',
      },
    ],
    [
      {
        key: 'description',
        label: 'Description',
        kind: 'textarea',
        hint: 'Optional',
        placeholder: 'How the date is defined, and where it appears in the contract',
      },
    ],
  ],
  api: {
    // Dates have no list, and a create needs its file (`fileDateConfig`); `EntityApi` still asks for both.
    search: () => Promise.reject(new Error('Dates have no list')),
    create: () => Promise.reject(new Error('A date is created on a contract file')),
    async update(uid, values): Promise<void> {
      await updateContractManagementDate({
        path: { uid },
        body: dateBody(values),
        throwOnError: true,
      });
    },
  },
});

/**
 * `dateConfig` creating on one contract file: on the revision given, or its
 * current one when null. The backend checks the file's dates still combine
 * (422), which the form shows as its save error.
 */
export function fileDateConfig(fileUid: string, revisionUid: string | null): EntityListConfig {
  return {
    ...dateConfig,
    api: {
      ...dateConfig.api,
      async create(values): Promise<EntityRow> {
        const { data } = await createContractManagementContractFileDate({
          path: { uid: fileUid },
          query: { revision_uid: revisionUid },
          body: dateCreateBody(values),
          throwOnError: true,
        });
        return data;
      },
    },
  };
}

/**
 * The party form, with email required: a party may exist without one, but
 * whoever signs needs it to receive the request. Companies hide the field,
 * so the rule only ever bites on people. Shared by the signing wizard's
 * recipients and the drafting wizard's signing slots.
 */
export const signingPartyConfig: EntityListConfig = {
  ...contractualPartyConfig,
  fields: contractualPartyConfig.fields.map(row =>
    row.map(field =>
      field.key === 'email' ? { ...field, required: true, hint: 'Needed to sign' } : field
    )
  ),
};

/** Route param (`entityType` prop) → config. */
export const entityConfigs: Partial<Record<ContractManagementEntityType, EntityListConfig>> = {
  clause: clauseConfig,
  obligation: obligationConfig,
  matter: matterConfig,
  'contractual-party': contractualPartyConfig,
};
